// San Jose State University: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
// #3859 (2026-10-11), page vs KB: the nonresident line now shows $471 a unit
// at 12 units a semester ($11,304), the load SJSU's admission steps use and
// the KB holds.
//  - SJSU's Common Data Set 2025-2026 (Institutional Research PDF,
//    20260113_CDS 2025 FINAL.pdf): C1 applicants and admits for Fall 2025
//    with the residency table, C8A (no use of SAT or ACT, and "not
//    considered for admission, even if submitted" for Fall 2027), C21/C22
//    (no early decision or early action). Quirks: C1's enrollee total by sex
//    (4,941) and the residency table's enrolled total (4,943) differ by two,
//    so no enrolled count is shown. Section G is blank; the cost comes from
//    the financial aid office.
//  - sjsu.edu admissions pages: Admission Deadlines (Fall 2027), Impaction
//    (the Fall 2027 admission cycle), CSU Eligibility Requirements (the CSU
//    a-g update for the 2027-28 application cycle), First-Time, First-Year
//    Steps to Admission (the Animation & Illustration drawing exercises).
//  - calstate.edu Application Dates & Deadlines was read too and agrees
//    (Fall 2027 priority filing August 1 to November 30, 2026); SJSU's own
//    deadlines page is the cited source.
//  - SJSU Financial Aid and Scholarship Office, Cost of Attendance: the
//    2026-2027 undergraduate budget for a student in campus housing. SJSU
//    publishes nonresident tuition only as $471 per unit, with no
//    out-of-state total, so that column's total is not published.
//  - College Scorecard school page, id 122755 (net price by family income;
//    the shared DEMO_KEY was rate-limited, so the figures are the ones the
//    official page loads from the same API).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'SJSU Common Data Set 2025-2026',
        url: 'https://www.sjsu.edu/irsa/docs/cds/20260113_CDS%202025%20FINAL.pdf',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'SJSU Admissions, Admission Deadlines',
        url: 'https://www.sjsu.edu/admissions/deadlines/index.php',
        read: '2026-10-01',
    },
    impaction: {
        label: 'SJSU Admissions, Impaction',
        url: 'https://www.sjsu.edu/admissions/impaction/index.php',
        read: '2026-10-01',
    },
    impactionResults: {
        label: 'SJSU Admissions, Fall 2025 Freshman Impaction Results by Major',
        url: 'https://www.sjsu.edu/admissions/impaction/freshmen-impaction-results/2025-fall.php',
        read: '2026-10-01',
    },
    eligibility: {
        label: 'SJSU Admissions, CSU Eligibility Requirements',
        url: 'https://www.sjsu.edu/admissions/freshman/admissions-requirements/csu-eligibility-requirements/index.php',
        read: '2026-10-01',
    },
    steps: {
        label: 'SJSU Admissions, First-Time, First-Year Steps to Admission',
        url: 'https://www.sjsu.edu/admissions/freshman/want-to-apply/freshman-steps-to-admission/index.php',
        read: '2026-10-01',
    },
    costs: {
        label: 'SJSU Financial Aid and Scholarship Office, Cost of Attendance',
        url: 'https://www.sjsu.edu/faso/process/cost-of-attendance.php',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?122755-San-Jose-State-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'san-jose-state-university',
    kbId: 'san_jose_state_university',
    name: 'San Jose State University',
    shortName: 'San Jose State',
    aliases: ['SJSU'],
    location: 'San Jose, CA',
    control: 'Public',
    lastChecked: '2026-10-11',
    sources: SOURCES,
    // From the KB's sourced branding (SJSU brand color guide); decoration only.
    branding: {
        primary: '#0055A2',
        secondary: '#E5A823',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/san_jose_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 37124,
        admitted: 29845,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'California residents', applied: 34882, admitted: 28137 },
            { group: 'Out-of-state', applied: 1432, admitted: 1070 },
            { group: 'International', applied: 810, admitted: 638 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'SJSU publishes no acceptance rate by major. Its Fall 2025 impaction results list instead the lowest impaction score each major admitted. Many majors had room for every CSU-eligible applicant; others took higher scores, such as Computer Engineering (4,920), Electrical and Mechanical Engineering (4,800 each), Aerospace Engineering (4,320) and Computer Science (3,440).',
        source: 'impactionResults',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'SJSU has no early decision or early action plan; the Common Data Set answers no to both. There is one Cal State Apply filing period.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Every SJSU major is impacted: meeting the CSU minimum does not guarantee admission. You apply to a major, and some majors ask for more:',
        rows: [
            { school: 'All majors', requirement: 'CSU-eligible applicants are ranked in their major by a-g GPA, with points added for graduating from a Santa Clara County high school, qualifying for the Cal State Apply fee waiver, being first generation, or military status. You must also name an alternate major.', source: 'impaction' },
            { school: 'College of Engineering', requirement: 'Applicants are ranked with a modified index that adds math GPA.', source: 'impaction' },
            { school: 'Animation & Illustration', requirement: 'Three short supplemental drawing exercises, due one week after the Cal State Apply deadline. Without them, you are considered for your alternate major only.', source: 'steps' },
        ],
        note: 'You cannot change your major during the application and admission process, and a change after admission is by petition only.',
        source: 'impaction',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application window', value: 'Oct 1 to Nov 30' },
        rows: [
            { plan: 'Cal State Apply', deadline: 'November 30', decision: null, binding: false },
        ],
        note: 'You apply through Cal State Apply. You can start the application on August 1, 2026; submission opens October 1, 2026, and the deadline is November 30, 2026. If admitted, the intent to enroll deadline is May 1, 2027.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'SAT and ACT scores are not used in SJSU admission decisions, even if you send them. If you are offered admission, you may send official scores, which are used for course placement.',
        source: 'eligibility',
        cdsLine: 'The Common Data Set agrees: it answers no to using SAT or ACT scores and marks them not considered for admission, even if submitted, for fall 2027 applicants.',
        cdsWhere: 'section C8A',
    },

    // Changes already in effect for this cycle: the CSU's a-g update applies
    // from the 2027-28 application cycle, the one for fall 2027 entry.
    currentChanges: [
        { topic: 'Language', line: 'The CSU language requirement now means two years, or through the second level, of the same language, or two one-year first-level courses. You may not mix semesters of different languages or use partial units to meet it.', source: 'eligibility' },
        { topic: 'Arts', line: 'The CSU arts requirement is now one year-long course or two semester courses in dance, music, theater, visual arts or interdisciplinary arts, both semesters finished before you graduate from high school.', source: 'eligibility' },
    ],
    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [9553, 9553] },
            { item: 'Nonresident tuition ($471 a unit, 24 units)', amounts: [0, 11304] },
            { item: 'Institutional related activities fee', amounts: [245, 245] },
            { item: 'Housing (campus housing)', amounts: [15543, 15543] },
            { item: 'Food', amounts: [7369, 7369] },
            { item: 'Books and supplies', amounts: [1004, 1004] },
            { item: 'Transportation', amounts: [1121, 1121] },
            { item: 'Miscellaneous and personal', amounts: [2900, 2900] },
        ],
        totals: [37735, 49039],
        note: 'Campus housing. Nonresidents pay $471 a unit on top of tuition; the figure shown is 12 units each semester, the load SJSU\'s own admission steps use.',
        source: 'costs',
        netPrice: {
            average: 14241,
            byIncome: [
                { income: '$0 to $30,000', amount: 10381 },
                { income: '$30,001 to $48,000', amount: 10669 },
                { income: '$48,001 to $75,000', amount: 12135 },
                { income: '$75,001 to $110,000', amount: 15813 },
                { income: 'Over $110,000', amount: 24449 },
            ],
            note: 'What first-year, full-time in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from SJSU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
